/**
 * Step 3 Sites: Table for selecting sites with country grouping
 *
 * Migrated from: lego/page/etmf/overview/update-study/Step3Sites.jsx
 */

import React, { useState } from 'react'
import { useServiceContext } from '../../../../root'
import { useDialogContext } from './context/useDialogContext'
import { RcmTableDataWrapper } from '../../../../components/rcm-table/RcmTableDataWrapper'
import {
  SelectAllRows,
  SelectRow,
  TableMultiSelectionWrapper,
  TableMultiSelectionConsumer
} from '../../../../components/rcm-table/TableMultiSelection'
import { RcmTable } from '../../../../components/rcm-table/RcmTable'
import { ErrorMessage } from 'formik'
import type { FormValues } from './context/DialogContext'
import _ from 'lodash'
import TableHeadRow from '../../../../components/table/TableHeadRow'
import { TableHeadCell } from '../../../../components/table/TableComponents'

interface Step3SitesProps {
  values: FormValues
  setFieldValue: (field: string, value: any) => void
  reloadController: (reloadFn: () => void) => void
}

export default function Step3Sites({ values, setFieldValue, reloadController }: Step3SitesProps) {
  const dialogData = useDialogContext()
  const context = useServiceContext()
  const { translate } = context.service.filter
  const [preselectedIds, setPreselectedIds] = useState<string[]>([])

  return (
    <div className="theme-sandman">
      {values.environment && (
        <RcmTableDataWrapper
          reloadController={reloadController}
          doPreload={true}
          loadData={(tableParams) => {
            const req = dialogData.loadSiteOptions(tableParams)
            req.then((data: any) => {
              // preselect checkboxes
              if (!data.isCached) {
                setFieldValue('sites', [])
              } else {
                setFieldValue('sites', values.sites)
                setPreselectedIds(values.sites)
              }
            })
            return req
          }}
          defaultSortBy={'NAME'}
          defaultSortDesc={false}
          persistenceKey={'ETMF_UPDATE_STUDY_STRUCTURE_SELECT_SITES'}
        >
          <TableMultiSelectionWrapper
            isSelectTotalEnabled={false}
            preselectedIds={preselectedIds}
            onChange={(ids) => {
              setFieldValue('sites', ids)
            }}
          >
            <RcmTable
              className="chita-table fsiTable"
              noResultsText="There are no entries that match your filter selections. Please adjust one or more filters and try again."
              rowKey="id"
              hidePaginate={true}
              HeadRow={
                <TableHeadRow>
                  <th className="clipped-cell with-checkbox">
                    <SelectAllRows />
                  </th>
                  <th className="clipped-cell with-checkbox"></th>
                  <TableHeadCell id="62d4d5c7-21d6-4240-b102-8fbf1c6769e7" sort={'NAME'}>
                    {translate('NAME')}
                  </TableHeadCell>
                </TableHeadRow>
              }
              Row={({ row, index }) => (
                <TableMultiSelectionConsumer>
                  {(tableMultiSelectionContext) => {
                    if (!tableMultiSelectionContext) return null
                    return (
                      <tr key={row.id}>
                        <td className="clipped-cell with-checkbox">
                          {row.isCountry && (
                            <SelectRow
                              row={row}
                              onChange={(e) => {
                                const { checked: isChecked } = e.target

                                tableMultiSelectionContext.setMultiSelectState((prevState) => {
                                  // apply country selection
                                  let newSelection = isChecked
                                    ? prevState.selectedIds.concat([row.id])
                                    : prevState.selectedIds.filter((val) => val !== row.id)

                                  // modify sites
                                  if (isChecked) {
                                    // select all sites
                                    newSelection = _.union(row.siteIds, newSelection)
                                  } else {
                                    // deselect all sites
                                    newSelection = _.difference(newSelection, row.siteIds)
                                  }

                                  return {
                                    ...prevState,
                                    selectedWholeResult: false,
                                    selectAllOnThisPage:
                                      isChecked &&
                                      _.union(prevState.currentlyDisplayedCheckboxes, newSelection).length ===
                                        newSelection.length,
                                    selectedIds: newSelection
                                  }
                                })
                              }}
                            />
                          )}
                        </td>
                        <td className="clipped-cell with-checkbox">
                          {row.isSite && (
                            <SelectRow
                              row={row}
                              onChange={(e) => {
                                const { checked: isChecked } = e.target

                                tableMultiSelectionContext.setMultiSelectState((prevState) => {
                                  // apply selection
                                  let newSelection = isChecked
                                    ? prevState.selectedIds.concat([row.id])
                                    : prevState.selectedIds.filter((val) => val !== row.id)

                                  if (isChecked) {
                                    // Check if all sites in this country are selected
                                    if (
                                      _.union(row.siteIds, newSelection).length === newSelection.length &&
                                      !_.includes(newSelection, row.countryId)
                                    ) {
                                      // select country
                                      newSelection = newSelection.concat([row.countryId])
                                    }
                                  } else {
                                    // deselect country
                                    newSelection = _.without(newSelection, row.countryId)
                                  }

                                  return {
                                    ...prevState,
                                    selectedWholeResult: false,
                                    selectAllOnThisPage:
                                      isChecked &&
                                      _.union(prevState.currentlyDisplayedCheckboxes, newSelection).length ===
                                        newSelection.length,
                                    selectedIds: newSelection
                                  }
                                })
                              }}
                            />
                          )}
                        </td>
                        <td title={row.name}>{row.isCountry ? <b>{row.name}</b> : row.name}</td>
                      </tr>
                    )
                  }}
                </TableMultiSelectionConsumer>
              )}
            />
          </TableMultiSelectionWrapper>
          <ErrorMessage name="sites" component="div" className="error-message text-danger" />
        </RcmTableDataWrapper>
      )}
    </div>
  )
}
